import { chordTones, type ChordTone } from '../chord/chord-tones.js'
import { mergeChordLine, sungName, type ChordLine, type ChordLineTone } from './guide-tones.js'
import type { VocalRange } from './types.js'

/** 1-3-5-3-1, with a sus chord's suspended note in the 3rd's place; a chord with neither is 1-5-1 */
const outlineTones = (tones: ChordTone[]) => {
  const find = (...degrees: number[]) => degrees.flatMap(degree => tones.filter(tone => tone.degree === degree))[0]
  const root = find(1)
  const middle = find(3, 4, 2)
  const fifth = find(5)
  if (!root || !fifth) {
    return root ? [root] : []
  }
  return middle ? [root, middle, fifth, middle, root] : [root, fifth, root]
}

/** Semitones above the root, each note above the one before it on the way up */
const ascending = (outline: ChordTone[]) =>
  outline.map(tone => (((tone.pitchClass - outline[0].pitchClass) % 12) + 12) % 12)

/** The root's octave that puts the arpeggio's middle nearest the middle of the range */
const placeRoot = (rootPitchClass: number, span: number, range: VocalRange) => {
  const centre = (range.low + range.high) / 2
  const octaves = Math.round((centre - span / 2 - rootPitchClass) / 12)
  return rootPitchClass + 12 * octaves
}

/**
 * Each chord sung up and down, 1-3-5-3-1, in a beat a note at 100 bpm (Bowie §1, plan M2 step 2.2).
 * A common tone where one chord ends and the next begins is sung once and held. N.C. and unknown
 * symbols are skipped; a slash bass is never sung.
 */
export const chordOutline = (chords: string[], range: VocalRange): ChordLine => {
  const sung = chords.flatMap(chord => {
    const outline = outlineTones(chordTones(chord) ?? [])
    if (outline.length === 0) {
      return []
    }
    const offsets = ascending(outline)
    const root = placeRoot(outline[0].pitchClass, Math.max(...offsets), range)
    return outline.map((tone, index): ChordLineTone => ({
      midi: root + offsets[index],
      name: sungName(tone),
      chord,
      degree: tone.degree,
    }))
  })
  return mergeChordLine(sung)
}
